import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export default function Profile() {
  const { user, addNotification, logout, emergencyLogin } = useApp();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showForcedReset, setShowForcedReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: Send Email, 2: Waiting for click

  const isBypass = user.id === 'emergency-admin';

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    // BYPASS TRIGGER
    if (formData.email === 'jenny@saasusecasetv.com' && formData.password === 'SMF_ADMIN_2024!') {
      emergencyLogin(formData.email);
      setShowForcedReset(true); // Trigger the forced reset UI immediately
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });
      if (error) throw error;
      addNotification("Welcome back!", "success");
    } catch (err) {
      addNotification(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const startForcedReset = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/#/reset-password`,
      });
      if (error) throw error;
      setResetStep(2);
      addNotification("Security token sent to your email!", "success");
    } catch (err) {
      addNotification(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!user.isLoggedIn) {
    return (
      <div className="p-4 pb-24 max-w-md mx-auto h-full flex items-center min-h-[80vh]">
        <div className="w-full space-y-6">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto border border-amber-500/20 shadow-xl">
              <SafeIcon icon={FiIcons.FiLogIn} className="text-3xl text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-white tracking-tight">Admin Login</h2>
          </div>

          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-3">
              <div className="relative">
                <SafeIcon icon={FiIcons.FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input 
                  required 
                  type="email" 
                  placeholder="Email Address" 
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3.5 pl-10 text-white outline-none focus:ring-2 focus:ring-amber-500/50" 
                  value={formData.email} 
                  onChange={e => setFormData({...formData, email: e.target.value})} 
                />
              </div>
              <div className="relative">
                <SafeIcon icon={FiIcons.FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input 
                  required 
                  type="password" 
                  placeholder="Password" 
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3.5 pl-10 text-white outline-none focus:ring-2 focus:ring-amber-500/50" 
                  value={formData.password} 
                  onChange={e => setFormData({...formData, password: e.target.value})} 
                />
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-4 rounded-xl shadow-lg transition-all flex items-center justify-center space-x-2">
              {loading ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> : <SafeIcon icon={FiIcons.FiArrowRight} />}
              <span>Sign In</span>
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 md:pb-8 max-w-lg mx-auto space-y-6">
      {/* FORCED RESET MODAL OVERLAY */}
      <AnimatePresence>
        {isBypass && showForcedReset && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            className="fixed inset-0 z-[60] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }} 
              animate={{ scale: 1, y: 0 }}
              className="bg-slate-900 border border-amber-500/30 p-8 rounded-3xl max-w-sm w-full shadow-2xl text-center space-y-6"
            >
              <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto border border-amber-500/20">
                <SafeIcon icon={FiIcons.FiShield} className="text-3xl text-amber-500" />
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white">Setup Real Password</h3>
                <p className="text-slate-400 text-xs mt-2 leading-relaxed">
                  You are currently using the <span className="text-amber-500 font-bold">Emergency Bypass</span>. 
                  To use the admin features permanently, you must set a private password.
                </p>
              </div>

              {resetStep === 1 ? (
                <button 
                  onClick={startForcedReset}
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-4 rounded-xl transition-all flex items-center justify-center space-x-2"
                >
                  {loading ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> : <SafeIcon icon={FiIcons.FiSend} />}
                  <span>Step 1: Send Secure Link</span>
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-xl text-emerald-400 text-[10px] font-bold uppercase tracking-widest animate-pulse">
                    Email Sent! Please check inbox...
                  </div>
                  <p className="text-slate-500 text-[10px]">
                    Click the link in the email. It will open a new tab where you can set your password. 
                    Once done, refresh this page to log in normally.
                  </p>
                  <button 
                    onClick={() => window.location.reload()}
                    className="w-full bg-slate-800 text-white font-bold py-3 rounded-xl border border-slate-700"
                  >
                    I've set it! Refresh App
                  </button>
                </div>
              )}
              
              <button 
                onClick={() => { setShowForcedReset(false); logout(); }}
                className="text-slate-500 text-[10px] uppercase font-bold tracking-widest hover:text-slate-300 transition-colors"
              >
                Cancel & Logout
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-slate-850 p-8 rounded-3xl border border-slate-800 text-center relative shadow-2xl">
        <div className="absolute top-0 right-0 p-4">
          <div className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border bg-blue-500/10 text-blue-400 border-blue-500/20">
            <SafeIcon icon={FiIcons.FiShield} />
            <span>Verified Admin</span>
          </div>
        </div>
        
        <div className="w-20 h-20 mx-auto bg-slate-800 rounded-2xl flex items-center justify-center mb-4 border-2 border-amber-500 shadow-xl rotate-3">
          <SafeIcon icon={FiIcons.FiUser} className="text-3xl text-amber-500" />
        </div>
        
        <h2 className="text-2xl font-bold text-white mb-1 tracking-tight">{user.username}</h2>
        <p className="text-slate-500 text-sm font-mono">{user.email}</p>

        <div className="mt-6 grid grid-cols-1 gap-3">
          <button onClick={() => navigate('/admin')} className="bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold py-4 rounded-xl transition-all shadow-lg uppercase tracking-wider">
            Command Center
          </button>
        </div>
      </div>

      <button onClick={logout} className="w-full bg-slate-800 hover:bg-rose-500/10 hover:text-rose-400 text-slate-400 font-bold py-4 rounded-2xl transition-all text-sm border border-slate-700 flex items-center justify-center space-x-2">
        <SafeIcon icon={FiIcons.FiLogOut} />
        <span>Log Out</span>
      </button>
    </div>
  );
}